import type { TypedMutationOnQueryStarted } from '@reduxjs/toolkit/query'
import { client as baseClient } from './baseApi/client'
import type { ContactUnitModel as BaseContactUnitModel } from './baseApi/client'
import type {
  ItemModel,
  ObjectContactModel,
  ObjectContactModelContactsQueryResultRead,
  PutContactsAndNameIdApiResponse,
  PutContactsAndNameIdApiArg,
} from './baseApi/client'
import type { baseQuery } from './baseQuery'
import type { OverrideResults } from './overrideResult'

export type * from './baseApi/client'
export { ItemContactSortColumn, SortOrder } from './baseApi/client'

// client-1-0.yaml declares ContactUnitModel.role `type: integer` with no
// named values; the API sends the role's string name.
export type ContactUnitModel = Omit<BaseContactUnitModel, 'role'> & {
  role?: string
}

// POST /metercontacts/query returns MeterContactModel; the schema says ObjectContactModel.
export type MeterContactModel = Omit<
  ObjectContactModel,
  'objectType' | 'objects' | 'objecttypeNo'
> & {
  metertype?: string | null
  meters?: ItemModel[] | null
}

export type MeterContactsResult = Omit<
  ObjectContactModelContactsQueryResultRead,
  'result'
> & {
  result?: MeterContactModel[] | null
}

export const onPutContactsAndNameIdStarted: TypedMutationOnQueryStarted<
  PutContactsAndNameIdApiResponse,
  PutContactsAndNameIdApiArg,
  typeof baseQuery,
  'portal'
> = async (
  { nameId, updateContactModel },
  { dispatch, getState, queryFulfilled }
) => {
  try {
    await queryFulfilled
  } catch {
    return
  }

  const cachedArgs = baseClient.util.selectCachedArgsForQuery(
    getState(),
    'postContactsList'
  )

  cachedArgs.forEach((arg) => {
    dispatch(
      baseClient.util.updateQueryData('postContactsList', arg, (draft) => {
        const resident = draft.result?.find(
          (contact) => contact.nameregisterId === nameId
        )
        if (!resident) return
        if (updateContactModel.email != null)
          resident.email = updateContactModel.email
        if (updateContactModel.phonenumber != null)
          resident.mobile = updateContactModel.phonenumber
      })
    )
  })
}

export const clientApi = baseClient.enhanceEndpoints<
  'Client' | 'BoardContactsList',
  OverrideResults<
    typeof baseClient,
    {
      getContactsAndNameIdUnits: ContactUnitModel[]
      postMetercontactsList: MeterContactsResult
    }
  >
>({
  addTagTypes: ['Client', 'BoardContactsList'],
  endpoints: {
    putContactsAndNameId: {
      onQueryStarted: onPutContactsAndNameIdStarted,
    },
    getClientsById: {
      providesTags: (_result, _error, arg) => [{ type: 'Client', id: arg.id }],
    },
    getClientsByIdSettings: {
      query: (queryArg) => ({
        url: `/client/Clients/${queryArg.id}/settings`,
        headers: { 'x-api-version': '3.0' },
      }),
    },
    putClientsByIdAdvisorportalaccess: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Client', id: arg.id },
      ],
    },
    postBoardcontactsList: {
      providesTags: (_result, _error, arg) => [
        { type: 'BoardContactsList', id: arg.clientId },
      ],
    },
    postBoardcontacts: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BoardContactsList', id: arg.clientId },
      ],
    },
    putBoardcontactsAndId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BoardContactsList', id: arg.clientId },
      ],
    },
    deleteBoardcontactsAndId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BoardContactsList', id: arg.clientId },
      ],
    },
  },
})

export const {
  useGetClientsByIdQuery,
  useGetClientsListQuery,
  useGetClientsQuery,
  useGetClientsByIdDocumentsQuery,
  useGetClientsByIdDocumentsAndDocumentIdDownloadQuery,
  useGetClientsByIdPrivacystatementQuery,
  useGetClientsByIdSettingsQuery,
  usePostPortalusersMutation,
  useGetPortalusersSummaryQuery,
  usePutContactsAndNameIdMutation,
  useGetContactsAndNameIdUnitsQuery,
  usePostContactsListQuery,
  usePostOthercontactsListQuery,
  usePostBoardcontactsListQuery,
  useGetBoardcontactsAndIdQuery,
  usePutBoardcontactsAndIdMutation,
  useDeleteBoardcontactsAndIdMutation,
  usePostBoardcontactsMutation,
  usePostObjectcontactsListQuery,
  usePostMetercontactsListQuery,
  useGetObjecttypesQuery,
  useGetMetertypesQuery,
  useGetClientsByClientNoActiveinfoQuery,
  usePutClientsByIdAdvisorportalaccessMutation,
  useGetClientsByClientNoOwnersQuery,
  useGetMinimizedClientsQuery,
  useGetBblcontactsQuery,
  usePutContactsAndNameIdElectroniccommunicationMutation,
} = clientApi
